import { create } from 'zustand';

export type ScaleMode = 'real' | 'log' | 'nested';

interface ScaleState {
  mode: ScaleMode;
  setMode: (m: ScaleMode) => void;
}

export const useScale = create<ScaleState>((set) => ({
  mode: 'nested',
  setMode: (m) => set({ mode: m }),
}));

/**
 * Transforme un rayon héliocentrique réel (UA) en unité scène, selon le mode :
 *   - real    : identité — 1 UA = 1 unité scène (Mercure≈0.39, Neptune≈30, illisible)
 *   - log     : log10(1 + r) × 6 — comprime les grandes distances
 *   - nested  : par paliers (jusqu'à Mars : ×3 ; jusqu'à Saturne : ×1.2 ; au-delà : ×0.6 + offset)
 */
export function scaleRadius(r_au: number, mode: ScaleMode): number {
  if (r_au <= 0) return 0;
  if (mode === 'real') return r_au;
  if (mode === 'log') return Math.log10(1 + r_au) * 6.5;
  // nested : palier inner / outer
  if (r_au <= 1.6) return r_au * 3;        // Mercure → Mars compressé visuellement
  if (r_au <= 10) return 1.6 * 3 + (r_au - 1.6) * 1.2; // Jupiter+Saturne
  return 1.6 * 3 + (10 - 1.6) * 1.2 + (r_au - 10) * 0.6; // Uranus, Neptune, TNO
}

/** Applique scaleRadius au vecteur position complet (norme conservée, direction inchangée). */
export function scaleVec3(
  v: readonly [number, number, number],
  mode: ScaleMode,
): [number, number, number] {
  const r = Math.hypot(v[0], v[1], v[2]);
  if (r === 0) return [0, 0, 0];
  const k = scaleRadius(r, mode) / r;
  return [v[0] * k, v[1] * k, v[2] * k];
}
